import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useRole } from "../hooks/useRole";
import { SettingsAPI, isDoctorProfile } from "../services/settings.service";
import { DoctorKycAPI, KycDocument } from "../services/doctorKyc.service";

// ── Types ────────────────────────────────────────────────────────────────────

interface Props {
  children: React.ReactNode;
  /** Custom content to show instead of the default KYC CTA. */
  fallback?: React.ReactNode;
}

type KycState =
  | null
  | { state: "none" }
  | { state: "pending" }
  | { state: "rejected"; rejectionReasons: string[] };

// ── CredentialGate ────────────────────────────────────────────────────────────

/**
 * Wraps any clinical action that requires prescribing rights.
 *
 * - While role is loading: renders nothing (fail-closed) to avoid briefly
 *   flashing prescribing UI to a not-yet-credentialed doctor.
 * - When can_prescribe === true OR role is not Doctor/Admin: renders children.
 * - When can_prescribe === false (Doctor or Admin): fetches KYC doc statuses
 *   and renders one of three states:
 *     - "none"     → amber card with "Complete KYC" CTA (blocks children)
 *     - "pending"  → dismissible amber banner above children (soft notice)
 *     - "rejected" → red card with rejection reasons + re-upload CTA (blocks)
 */
export const CredentialGate: React.FC<Props> = ({ children, fallback }) => {
  const navigate = useNavigate();
  const { role, can_prescribe, isDoctor, isAdmin, isLoaded } = useRole();

  const [kycState, setKycState] = useState<KycState>(null);
  const [kycLoaded, setKycLoaded] = useState(false);

  const gateApplies = isDoctor || isAdmin;
  const needsGate = isLoaded && gateApplies && !can_prescribe;

  // Fetch KYC doc statuses only when the gate is actually needed.
  useEffect(() => {
    if (!needsGate) return;

    let cancelled = false;

    (async () => {
      try {
        const profile = await SettingsAPI.getProfile();
        if (cancelled) return;

        if (!isDoctorProfile(profile) || !profile.doctor_id) {
          setKycLoaded(true);
          return;
        }

        const docs: KycDocument[] = await DoctorKycAPI.list(profile.doctor_id);
        if (cancelled) return;

        const rejected = docs.filter((d) => d.status === "rejected");
        if (rejected.length > 0) {
          const reasons = rejected
            .map((d) => d.rejection_reason)
            .filter((r): r is string => r !== null && r.trim() !== "");
          setKycState({ state: "rejected", rejectionReasons: reasons });
        } else if (docs.some((d) => d.status === "submitted")) {
          setKycState({ state: "pending" });
        } else {
          setKycState({ state: "none" });
        }
      } catch {
        // On fetch failure fall back to "none" — the "no docs" CTA is safe.
        if (!cancelled) setKycState({ state: "none" });
      } finally {
        if (!cancelled) setKycLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [needsGate]);

  // Wait for role to resolve — useRole() reads synchronously from localStorage
  // so this is a no-op on warm sessions; avoids briefly rendering prescribing
  // UI for a non-credentialed doctor while the async fallback is in-flight.
  if (!isLoaded) {
    return null;
  }

  // Empty role means getMe() failed — unknown identity; don't render clinical content.
  if (role === "") return null;

  // Only gate Doctors and Admins; other known roles (Receptionist, PlatformAdmin) pass through.
  if (!gateApplies || can_prescribe) {
    return <>{children}</>;
  }

  // can_prescribe === false for a Doctor or Admin: show gate UI.
  if (fallback) {
    return <>{fallback}</>;
  }

  // KYC fetch in-flight — show nothing rather than briefly granting access.
  if (!kycLoaded || kycState === null) {
    return null;
  }

  // ── Shared icon components ───────────────────────────────────────────────────
  const IconWarning = () => (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 shrink-0">
      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
    </svg>
  );

  // Shared wrapper: banner above children (never hide children — defense in depth
  // via backend 403 if state is stale; buttons always visible per design).
  const withBanner = (banner: React.ReactNode) => (
    <div className="space-y-3">
      {banner}
      <div className="pointer-events-none opacity-50 select-none">{children}</div>
    </div>
  );

  // ── Pending ──────────────────────────────────────────────────────────────────
  if (kycState.state === "pending") {
    return withBanner(
      <div className="bg-[#fffbeb] border border-[#ea580c] rounded-xl flex items-center justify-between px-4 py-3.5 gap-3">
        <div className="flex gap-3 items-start min-w-0">
          <span className="text-[#ea580c] mt-0.5"><IconWarning /></span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#ea580c]">Pending verification — reviewed same business day</p>
            <p className="text-xs text-[#111827] mt-0.5">
              Your credentials are under review. Prescribing will be unlocked once confirmed.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/settings?section=compliance")}
          className="shrink-0 bg-[#ea580c] text-white text-sm font-semibold px-3.5 py-2 rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap"
        >
          View KYC status
        </button>
      </div>
    );
  }

  // ── Rejected — hard block with reasons ──────────────────────────────────────
  if (kycState.state === "rejected") {
    const { rejectionReasons } = kycState;
    return withBanner(
      <div className="bg-red-50 border border-red-300 rounded-xl flex items-center justify-between px-4 py-3.5 gap-3">
        <div className="flex gap-3 items-start min-w-0">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 shrink-0 text-red-600 mt-0.5">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
          </svg>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-red-700">Document verification failed</p>
            {rejectionReasons.length > 0 && (
              <ul className="text-xs text-red-600 mt-0.5 space-y-0.5 list-disc list-inside">
                {rejectionReasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/settings?section=compliance")}
          className="shrink-0 bg-red-600 text-white text-sm font-semibold px-3.5 py-2 rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
        >
          Re-upload documents
        </button>
      </div>
    );
  }

  // ── None — no docs submitted yet ─────────────────────────────────────────────
  return withBanner(
    <div className="bg-[#ffedd5] border border-[#ea580c] rounded-xl flex items-center justify-between px-4 py-3.5 gap-3">
      <div className="flex gap-3 items-start min-w-0">
        <span className="text-[#ea580c] mt-0.5"><IconWarning /></span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#ea580c]">Complete KYC to prescribe</p>
          <p className="text-xs text-[#ea580c] mt-0.5">
            Your credentials are pending verification. Finish KYC to start visits, prescribe and use AI Scribe.
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => navigate("/settings?section=compliance")}
        className="shrink-0 bg-[#ea580c] text-white text-sm font-semibold px-3.5 py-2 rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap"
      >
        Complete KYC →
      </button>
    </div>
  );
};

// ── handleCredentialError ─────────────────────────────────────────────────────

/**
 * Call in API error handlers to detect and surface credential errors.
 *
 * Returns `true`  when the error carries error_code "CLINICAL_CREDENTIAL_REQUIRED"
 *                  (side-effect: shows a toast and navigates to compliance settings).
 * Returns `false` for all other errors.
 *
 * Usage:
 *   .catch((err) => {
 *     if (handleCredentialError(err)) return;
 *     // handle other errors…
 *   })
 *
 * NOTE: This is a plain function — it can only call navigate/toast through a
 * re-usable callback. For navigate support outside React trees, pass a
 * `navigate` function as the second argument; the toast fires regardless.
 */
export function handleCredentialError(
  error: unknown,
  navigate?: (path: string) => void
): boolean {
  // Support both Axios-style (error.response.data.error_code) and
  // fetch-style (error.data.error_code) response shapes.
  const code =
    (error as { response?: { data?: { error_code?: string } } })?.response?.data
      ?.error_code ??
    (error as { data?: { error_code?: string } })?.data?.error_code;

  if (code !== "CLINICAL_CREDENTIAL_REQUIRED") {
    return false;
  }

  toast.warning(
    "Prescribing credentials required — please complete your KYC.",
    { toastId: "credential-required" } // deduplicate repeat toasts
  );

  if (navigate) {
    navigate("/settings?section=compliance");
  }

  return true;
}

export default CredentialGate;
